import {
    ContactId,
    IAdditionalClient,
    IPlacement,
    ReferenceContactId,
    SAME_FOR_BOTH_STR,
} from "@sp-crm/core";
import { InputFormBlock } from "components/app/input-form-block";
import { Checkbox } from "components/ui/checkbox";
import { CrmPersona } from "components/ui/persona";
import { ClientContactRelationship } from "generated/graphql";
import React, { FunctionComponent, useCallback } from "react";
import { ClientContacts } from "../client-contacts";
import AdditionalClientContactList from "./additional-client-contact-list";
import { CareProvidersHeader } from "./care-providers-header";

type MultipleClientsProps = {
    placement: IPlacement;
    additionalClient: IAdditionalClient;
    handleSameForBothClientsCheckbox: (newValue: boolean) => void;
    physicianContactsSameAsPrimaryClient: boolean;
    onAddNewAdditionalClientContact: () => void;
    onLinkNewAdditionalClientContact: (referenceContactId: ReferenceContactId) => void;
    editAdditionalClientContact: (contactId: ContactId) => void;
    deleteAdditionalClientContact: (contactId: ContactId) => void;
};

const MultipleClients: FunctionComponent<MultipleClientsProps> = ({
    placement,
    additionalClient,
    handleSameForBothClientsCheckbox,
    physicianContactsSameAsPrimaryClient,
    onAddNewAdditionalClientContact,
    onLinkNewAdditionalClientContact,
    editAdditionalClientContact,
    deleteAdditionalClientContact,
}) => {
    const handleNewAdditionalClientContactClick = useCallback(
        () => onAddNewAdditionalClientContact(),
        [onAddNewAdditionalClientContact],
    );

    const handleLinkAdditionalClientContactClick = useCallback(
        (referenceContactId: ReferenceContactId) =>
            onLinkNewAdditionalClientContact(referenceContactId),
        [onLinkNewAdditionalClientContact],
    );

    return (
        <InputFormBlock additionalClassName="space-y-4">
            <CareProvidersHeader>
                <div>
                    <Checkbox
                        onChange={e => handleSameForBothClientsCheckbox(e.target.checked)}
                        checked={physicianContactsSameAsPrimaryClient}
                        label={SAME_FOR_BOTH_STR}
                    />
                </div>
            </CareProvidersHeader>

            {physicianContactsSameAsPrimaryClient ? (
                <>
                    <ClientContacts
                        header={
                            <CrmPersona
                                name={placement.formatClientAndAdditionalClientNames()}
                                size="small"
                            />
                        }
                        clientId={placement.id}
                        relationship={ClientContactRelationship.CareProvider}
                    />
                </>
            ) : (
                <>
                    <ClientContacts
                        header={
                            <CrmPersona
                                name={placement.formatName(placement.name)}
                                size="small"
                            />
                        }
                        clientId={placement.id}
                        relationship={ClientContactRelationship.CareProvider}
                    />
                    <AdditionalClientContactList
                        clientId={placement.id}
                        name={placement.formatName(additionalClient.name)}
                        contacts={additionalClient.physicianContacts}
                        onAddNewContact={handleNewAdditionalClientContactClick}
                        onLinkContact={handleLinkAdditionalClientContactClick}
                        editContact={editAdditionalClientContact}
                        deleteContact={deleteAdditionalClientContact}
                    />
                </>
            )}
        </InputFormBlock>
    );
};

export default MultipleClients;
